import { useEffect, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../../../components/Header';
import {
  createShiftRosterDutyCode,
  getShiftRosterDutyCodes,
  updateShiftRosterDutyCode,
  deleteShiftRosterDutyCode,
} from '../../../services/shiftRoasterService';
import Loader from '../../../components/Loader/Loader';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';
import TimePicker from '../../../components/TimePicker/TimePicker';
interface DutyCode {
  id: number;
  code: string;
  title: string;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ open, onClose, title, children }: ModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl p-6 w-80" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-base font-semibold text-gray-900 mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

interface DutyCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: any) => Promise<void>;
  duty?: DutyCode | null;
}

function DutyCodeModal({ isOpen, onClose, onSubmit, duty }: DutyCodeModalProps) {
  const isEditing = Boolean(duty);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [errors, setErrors] = useState({
    code: '',
    label: '',
  });

  useEffect(() => {
    if (!isOpen) return;

    setCode(duty?.code ?? '');
    setLabel(duty?.title ?? '');
    setStartTime(duty?.start_time?.slice(0, 5) ?? '');
    setEndTime(duty?.end_time?.slice(0, 5) ?? '');
    setIsActive(duty?.is_active ?? true);
    setErrors({ code: '', label: '' });
  }, [duty, isOpen]);

  function reset() {
    setCode('');
    setLabel('');
    setStartTime('');
    setEndTime('');
    setIsActive(true);

    setErrors({
      code: '',
      label: '',
    });
  }

  async function handleSubmit() {
    const newErrors = {
      code: '',
      label: '',
    };

    let hasError = false;

    if (!code.trim()) {
      newErrors.code = 'Code is required';
      hasError = true;
    }

    if (!label.trim()) {
      newErrors.label = 'Label is required';
      hasError = true;
    }

    setErrors(newErrors);

    if (hasError) return;

    const payload = {
      ...(!isEditing || code.trim() !== duty?.code ? { code: code.trim() } : {}),
      title: label.trim(),
      start_time: startTime,
      end_time: endTime,
      is_active: isActive,
    };

    try {
      setIsSubmitting(true);

      await onSubmit(payload);

      reset();

      onClose();
    } catch {
      // The parent displays the API error; keep the form open so it can be corrected.
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal open={isOpen} onClose={handleClose} title={isEditing ? 'Update Duty Code' : 'Add Duty Code'}>
      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Code
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={code}
            onChange={(e) => {
              setCode(e.target.value);

              if (e.target.value.trim()) {
                setErrors((prev) => ({
                  ...prev,
                  code: '',
                }));
              }
            }}
            placeholder="e.g. M"
            className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
              errors.code ? 'border border-red-500' : 'border border-gray-300 focus:border-red-400'
            }`}
          />

          {errors.code && <p className="text-red-500 text-xs mt-1">{errors.code}</p>}
        </div>

        <div>
          <label className="block text-xs text-gray-500 mb-1">
            Label
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => {
              setLabel(e.target.value);

              if (e.target.value.trim()) {
                setErrors((prev) => ({
                  ...prev,
                  label: '',
                }));
              }
            }}
            placeholder="e.g. Morning"
            className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none ${
              errors.label ? 'border border-red-500' : 'border border-gray-300 focus:border-red-400'
            }`}
          />

          {errors.label && <p className="text-red-500 text-xs mt-1">{errors.label}</p>}
        </div>

        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">Start time</label>
            <TimePicker value={startTime} onChange={setStartTime} />
          </div>
          <div className="flex-1">
            <label className="block text-xs text-gray-500 mb-1">End time</label>
            <TimePicker value={endTime} onChange={setEndTime} />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-red-500" />
          Active
        </label>
      </div>

      <div className="flex justify-end gap-2 mt-5">
        <button onClick={handleClose} className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-1.5 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center min-w-[110px]"
        >
          {isSubmitting ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              {isEditing ? 'Updating...' : 'Adding...'}
            </>
          ) : isEditing ? (
            'Update'
          ) : (
            'Add code'
          )}
        </button>
      </div>
    </Modal>
  );
}

interface DutyCardProps {
  duty: DutyCode;
  onEditClick: (duty: DutyCode) => void;
  onDeleteClick: (duty: DutyCode) => void;
}

function DutyCard({ duty, onEditClick, onDeleteClick }: DutyCardProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 relative ${duty.is_active ? '' : 'opacity-60'}`}
    >
      {/* Code */}
      <span className="text-2xl font-bold text-gray-900 min-w-[48px]">{duty.code}</span>

      <div className="flex flex-col">
        <span className="text-xs text-gray-400 font-medium">{duty.title}</span>

        <span className="text-sm font-semibold text-gray-800">
          {duty.start_time || '--:--'} - {duty.end_time || '--:--'}
        </span>
        {!duty.is_active && <span className="text-[11px] font-medium text-gray-500">Inactive</span>}
      </div>

      {/* Menu */}
      <div className="absolute top-3 right-3 flex items-center gap-1">
        <button
          type="button"
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          onClick={() => onEditClick(duty)}
          aria-label={`Edit ${duty.code}`}
          title="Edit duty code"
        >
          <Pencil size={15} />
        </button>
        <button
          className="text-gray-300 hover:text-gray-500 text-lg leading-none px-1"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label="Options"
        >
          ⋮
        </button>
        {menuOpen && (
          <div className="absolute right-0 top-6 bg-white border border-gray-200 rounded-lg shadow-md z-10 w-28">
            <button
              className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 rounded-lg"
              // onClick={() => {
              //   onRemove();
              //   setMenuOpen(false);
              // }}
              onClick={() => {
                onDeleteClick(duty);
                setMenuOpen(false);
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DutyCodeTab() {
  const [dutyCodes, setDutyCodes] = useState<DutyCode[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [errorModal, setErrorModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState({
    title: '',
    message: '',
  });
  const [activeTab, setActiveTab] = useState<'Group' | 'Duty Code'>('Duty Code');
  const navigate = useNavigate();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDuty, setSelectedDuty] = useState<DutyCode | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [loading, setLoading] = useState(false);

  function handleClick(tab: 'Group' | 'Duty Code') {
    setActiveTab(tab);
    if (tab === 'Group') {
      navigate('/ShiftRoster/configuration');
    }
    if (tab === 'Duty Code') {
      navigate('/ShiftRoster/configuration/dutycode');
    }
  }

  const fetchDutyCodes = async () => {
    try {
      setLoading(true);

      const response = await getShiftRosterDutyCodes();

      setDutyCodes(response.data || []);
    } catch (error) {
      console.error('Error fetching duty codes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDutyCodes();
  }, []);

  const handleCreateDutyCode = async (payload: any) => {
    try {
      await createShiftRosterDutyCode(payload);

      setModalOpen(false);

      await fetchDutyCodes();
    } catch (error: any) {
      const response = error?.response?.data;

      let message = 'Something went wrong.';

      if (response?.errors) {
        const firstKey = Object.keys(response.errors)[0];

        if (firstKey) {
          message = response.errors[firstKey][0];
        }
      } else if (response?.message) {
        message = response.message;
      }

      setErrorMessage({
        title: 'Creation Failed',
        message,
      });

      setErrorModal(true);

      console.error('Error creating duty code:', error);
      throw error;
    }
  };

  const handleUpdateDutyCode = async (payload: any) => {
    if (!selectedDuty) return;

    try {
      await updateShiftRosterDutyCode(selectedDuty.id, payload);
      setEditModalOpen(false);
      setSelectedDuty(null);
      await fetchDutyCodes();
    } catch (error: any) {
      const response = error?.response?.data;
      const firstKey = response?.errors ? Object.keys(response.errors)[0] : undefined;
      const message = firstKey ? response.errors[firstKey][0] : response?.message || 'Something went wrong.';

      setErrorMessage({
        title: 'Update Failed',
        message,
      });
      setErrorModal(true);
      console.error('Error updating duty code:', error);
      throw error;
    }
  };
  const handleDeleteDutyCode = async () => {
    if (!selectedDuty) return;
    setIsDeleting(true);
    try {
      await deleteShiftRosterDutyCode(selectedDuty.id);

      setShowDeleteModal(false);
      setSelectedDuty(null);

      // Refresh the list
      await fetchDutyCodes();
    } catch (error) {
      console.error('Error deleting duty code:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <Header title="Shift Roster Configuration" showBackButton />

      <div className="flex-1 flex gap-4 p-6 overflow-hidden">
        {/* Left column: Tabs + Duty Codes */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          {/* Tabs + Add Duty Code */}
          <div className="flex items-center gap-2 mb-5 shrink-0">
            {(['Group', 'Duty Code'] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => handleClick(tab)}
                className={`px-4 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-red-50 border-red-400 text-red-500'
                    : 'bg-white border-gray-300 text-gray-500 hover:border-gray-400'
                }`}
              >
                {tab === 'Group' ? 'Master Shift Personnel' : tab}
              </button>
            ))}

            <button
              onClick={() => setModalOpen(true)}
              className="ml-auto flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <span className="text-base leading-none">+</span> Duty Code
            </button>
          </div>

          {/* Duty Codes Grid */}
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <Loader />
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {dutyCodes.map((duty) => (
                  <DutyCard
                    key={duty.id}
                    duty={duty}
                    onEditClick={(duty) => {
                      setSelectedDuty(duty);
                      setEditModalOpen(true);
                    }}
                    onDeleteClick={(duty) => {
                      setSelectedDuty(duty);
                      setShowDeleteModal(true);
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Duty Code Modal */}
      <DutyCodeModal isOpen={modalOpen} onClose={() => setModalOpen(false)} onSubmit={handleCreateDutyCode} />

      <DutyCodeModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedDuty(null);
        }}
        onSubmit={handleUpdateDutyCode}
        duty={selectedDuty}
      />

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-[400px] p-6">
            <h2 className="text-lg font-semibold mb-3">Delete Confirmation</h2>

            <p className="text-sm text-gray-600 mb-6">Are you sure you want to delete this item?</p>

            <div className="flex justify-end gap-3">
              <button
                className="px-4 py-2 border rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setShowDeleteModal(false)}
                disabled={isDeleting}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md flex items-center justify-center gap-2 min-w-[90px] disabled:bg-red-400 disabled:cursor-not-allowed"
                onClick={handleDeleteDutyCode}
                disabled={isDeleting}
              >
                {isDeleting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal
        isOpen={errorModal}
        onClose={() => setErrorModal(false)}
        title={errorMessage.title}
        message={errorMessage.message}
        buttonText="OK"
        onButtonClick={() => setErrorModal(false)}
      />
    </div>
  );
}
